// hooks/useScrollReveal.ts
"use client";

import { useEffect, useRef, useState } from "react";

interface UseScrollRevealOptions {
  threshold?: number;
  rootMargin?: string;
}

/**
 * Lightweight, crash-proof scroll-reveal hook.
 * Replaces GSAP ScrollTrigger for simple fade/slide-in effects.
 *
 * Why this instead of ScrollTrigger:
 * - No dependency on trigger-position math staying valid across
 *   layout shifts (late images/iframes/fonts) — IO recalculates itself.
 * - Built-in fallback timeout: content can NEVER get stuck at
 *   opacity:0 forever, which is what was happening before.
 * - Only opacity/transform are animated — cheap, GPU-composited,
 *   no layout or paint cost like `filter: blur()` has.
 */
export function useScrollReveal<T extends HTMLElement>(
  options: UseScrollRevealOptions = {}
) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reduced) {
      setIsVisible(true);
      return;
    }

    // Already in (or near) the viewport on mount — reveal immediately
    // instead of waiting on an observer that may fire late or not at all.
    const rect = node.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.9 && rect.bottom > 0) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: options.threshold ?? 0.15,
        rootMargin: options.rootMargin ?? "0px 0px -10% 0px",
      }
    );
    observer.observe(node);

    // Safety net — guarantees content is never permanently hidden.
    const fallback = setTimeout(() => setIsVisible(true), 2500);

    return () => {
      observer.disconnect();
      clearTimeout(fallback);
    };
  }, [options.threshold, options.rootMargin]);

  return { ref, isVisible };
}